/*
 * JOINED TEXT FIELDS
 *
 * Presents a title input and body textarea as one accessible bordered popup control.
 *
 * Recent changes:
 * - Added the feature-neutral joined field pattern shared by task and note popups.
 * - Supports an opt-in required body so notes can require detail without changing optional task notes.
 */

import type { ClipboardEventHandler } from 'react';
import { THEME_CLASSES } from '../../constants/themeConstants';
import { POPUP_LABEL_CLASS_NAME } from './popupStyles';

interface JoinedTextFieldsProps {
  idPrefix: string;
  groupLabel: string;
  titleLabel: string;
  bodyLabel: string;
  titlePlaceholder: string;
  bodyPlaceholder: string;
  title: string;
  body: string;
  onTitleChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  onTitlePaste?: ClipboardEventHandler<HTMLInputElement>;
  onBodyPaste?: ClipboardEventHandler<HTMLTextAreaElement>;
  disabled?: boolean;
  readOnly?: boolean;
  requiredTitle?: boolean;
  requiredBody?: boolean;
  invalid?: boolean;
  bodyRows?: number;
  bodyIdSuffix?: string;
  describedBy?: string;
  className?: string;
  surfaceClassName?: string;
  bodyClassName?: string;
  testId?: string;
}

export default function JoinedTextFields({
  idPrefix,
  groupLabel,
  titleLabel,
  bodyLabel,
  titlePlaceholder,
  bodyPlaceholder,
  title,
  body,
  onTitleChange,
  onBodyChange,
  onTitlePaste,
  onBodyPaste,
  disabled = false,
  readOnly = false,
  requiredTitle = false,
  requiredBody = false,
  invalid = false,
  bodyRows = 9,
  bodyIdSuffix = 'body',
  describedBy,
  className = '',
  surfaceClassName = '',
  bodyClassName = '',
  testId,
}: JoinedTextFieldsProps) {
  const unavailableClassName = disabled || readOnly ? 'cursor-not-allowed opacity-70' : '';

  return (
    <div className={`min-w-0 ${className}`}>
      <div className={POPUP_LABEL_CLASS_NAME}>{groupLabel}</div>
      <div
        data-testid={testId}
        className={`overflow-hidden rounded-xl border bg-white transition-shadow focus-within:ring-2 focus-within:ring-blue-500/30 dark:bg-slate-950 ${
          invalid ? 'border-rose-400 dark:border-rose-700' : THEME_CLASSES.input.border
        } ${surfaceClassName}`}
      >
        <label className="sr-only" htmlFor={`${idPrefix}-title`}>{titleLabel}</label>
        <input
          id={`${idPrefix}-title`}
          value={title}
          onChange={(event) => onTitleChange(event.target.value)}
          onPaste={onTitlePaste}
          placeholder={titlePlaceholder}
          required={requiredTitle}
          disabled={disabled}
          readOnly={readOnly}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={`block h-12 w-full border-0 bg-transparent px-4 text-sm ${THEME_CLASSES.input.text} placeholder:text-slate-400 focus:outline-none focus:ring-0 dark:placeholder:text-slate-500 ${unavailableClassName}`}
        />
        <div className={`border-t ${THEME_CLASSES.input.border}`} />
        <label className="sr-only" htmlFor={`${idPrefix}-${bodyIdSuffix}`}>{bodyLabel}</label>
        <textarea
          id={`${idPrefix}-${bodyIdSuffix}`}
          value={body}
          onChange={(event) => onBodyChange(event.target.value)}
          onPaste={onBodyPaste}
          placeholder={bodyPlaceholder}
          rows={bodyRows}
          required={requiredBody}
          disabled={disabled}
          readOnly={readOnly}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={`block min-h-[10rem] w-full resize-y border-0 bg-transparent px-4 py-3 text-sm leading-6 ${THEME_CLASSES.input.text} placeholder:text-slate-400 focus:outline-none focus:ring-0 dark:placeholder:text-slate-500 ${unavailableClassName} ${bodyClassName}`}
        />
      </div>
    </div>
  );
}

export type { JoinedTextFieldsProps };
